<template>
  <v-navigation-drawer
    v-if="localShow"
    v-model="localShow"
    color="white"
    absolute
    temporary
    persistent
    :scrim="false"
    location="left"
    width="22vw"
    class="filter-drawer elevation-4"
  >
    <div class="sidesheet px-4 py-4">
      <div class="d-flex justify-space-between align-center">
        <div class="zamg-text-headline">
          {{ t('side_sheet.header') }}
        </div>
        <v-spacer />
        <v-btn
          icon
          data-testid="filter-drawer-close"
          variant="text"
          @click.stop="toggleDrawer"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider opacity="25" />
      <v-text-field
        v-model="localFilters.id"
        class="mt-4"
        :label="t('station_table.table.id')"
        :hint="t('hints.text_can_be_separated_by_comma')"
        data-testid="filter-id"
        variant="filled"
        persistent-hint
        density="compact"
        @input="(event) => putFilterDebounced({ id: cleanString(event.target.value) })"
      />
      <v-text-field
        v-model="localFilters.name"
        class="mt-4"
        :label="t('station_table.table.name')"
        :hint="t('hints.text_can_be_separated_by_comma')"
        data-testid="filter-name"
        variant="filled"
        persistent-hint
        density="compact"
        @input="(event) => putFilterDebounced({ name: cleanString(event.target.value) })"
      />
      <v-select
        v-model="localFilters.states"
        class="mt-4"
        :label="t('station_table.table.state')"
        :items="localizedStates"
        item-title="label"
        item-value="value"
        data-testid="filter-state"
        multiple
        variant="filled"
        hide-details
        density="compact"
        @update:model-value="(value) => putFilterDebounced({ states: cleanStates(value) })"
      />
      <text-field-with-date-picker
        :value="localFilters.startDate"
        class="mt-4"
        :label="t('start_date')"
        :show-hint="false"
        :testid="'filter-drawer-start-date'"
        :allowed-date-range="[
          datasetStore.metadata?.startDate,
          datasetStore.metadata?.endDate,
        ]"
        :selected-date-is-start-date="true"
        :reference-date="localFilters.endDate"
        :text-field-props="{ filled: true, density: 'compact' }"
        @date-selected="(value) => putFilterDebounced({ startDate: cleanDate(value) })"
      />
      <text-field-with-date-picker
        :value="localFilters.endDate"
        class="mt-4"
        :label="t('end_date')"
        :show-hint="false"
        :testid="'filter-drawer-end-date'"
        :allowed-date-range="[
          datasetStore.metadata?.startDate,
          datasetStore.metadata?.endDate,
        ]"
        :selected-date-is-start-date="false"
        :reference-date="localFilters.startDate"
        :text-field-props="{ filled: true, density: 'compact' }"
        @date-selected="(value) => putFilterDebounced({ endDate: cleanDate(value) })"
      />
      <div class="slider-with-header mt-4">
        <div class="d-flex">
          <div class=".zamg-text-small-continous">
            {{ t('side_sheet.altitudeSliderLabel') }}
          </div>
          <v-spacer />
          <div class="zamg-text-label zamg-text-small-continous">
            {{ altitudeFormatted }}
          </div>
        </div>
        <v-range-slider
          v-model="localFilters.altitude"
          :max="defaultAltitude?.[1]"
          :min="defaultAltitude?.[0]"
          data-testid="filter-station-altitude"
          step="50"
          hide-details
          density="compact"
          color="primary"
          @update:model-value="(value) => putFilterDebounced({ altitude: cleanAltitude(value) })"
        />
      </div>
      <v-checkbox
        v-model="localFilters.onlyWithSunshine"
        class="mt-4"
        hide-details
        data-testid="filter-station-only-with-sunshine"
        :label="t('station_table.only_stations_with') + ' ' + t('station_table.table.hasSunshine')"
        density="compact"
        @update:model-value="(value) => putFilterDebounced({ onlyWithSunshine: cleanBoolean(value) })"
      />
      <v-checkbox
        v-model="localFilters.onlyWithGlobalRadiation"
        class="mt-4"
        hide-details
        data-testid="filter-station-only-with-global-radiation"
        :label="t('station_table.only_stations_with') + ' ' + t('station_table.table.hasGlobalRadiation')"
        density="compact"
        @update:model-value="(value) => putFilterDebounced({ onlyWithGlobalRadiation: cleanBoolean(value) })"
      />
      <v-checkbox
        v-model="localFilters.onlyCurrent"
        class="mt-4"
        hide-details
        data-testid="filter-station-only-current"
        :label="t('station_table.only_current_stations')"
        density="compact"
        @update:model-value="(value) => putFilterDebounced({ onlyCurrent: cleanBoolean(value) })"
      />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { Filters } from '@/stores/station/station-model';
import StationStore, { copyFilters } from '@/stores/station/station-module';
import StationDatasetStore from '@/stores/dataset/station-dataset-module';
import DatasetStore from '@/stores/dataset/dataset-module';
import TextFieldWithDatePicker from '@/components/common/TextFieldWithDatePicker.vue';
import { scalarArraysAreEqual } from '@/util/object';
import { useI18n } from "vue-i18n";
import { Bundesland } from '@/api/out';

type BundeslandKey = keyof typeof Bundesland;

export default defineComponent({
  name: 'FilterDrawer',
  components: { TextFieldWithDatePicker },
  props: {
    show: { type: Boolean, required: true },
  },
  emits: ['update:show'],
  setup( props, { emit } ) {
    const { t } = useI18n();

    const stationStore = StationStore;
    const datasetStore = DatasetStore;
    const stationDatasetStore = StationDatasetStore;

    const timerId = ref<ReturnType<typeof setTimeout> | null>(null);

    const localShow = ref(props.show);

    watch(() => props.show, (val) => {
      localShow.value = val;
    });

    watch(localShow, (val) => {
      emit('update:show', val);
    });


    const defaultAltitude = computed(() => {
      return stationDatasetStore.metadata?.stationAltitudeRange;
    });

    const filters = computed<Filters>(() => {
      const f = copyFilters(stationStore.filters);
      if (f.altitude === undefined) {
        f.altitude = defaultAltitude.value ? [...defaultAltitude.value] : undefined;
      }
      return f;
    });

    const localFilters = ref({ ...filters.value });

    const altitudeFormatted = computed(() => {
      const altitude = filters.value.altitude;
      return altitude ? `${altitude[0]}m - ${altitude[1]}m` : '';
    });

    const localizedStates = computed(() => {
      return (Object.keys(Bundesland) as BundeslandKey[]).map((key) => ({
        value: Bundesland[key],
        label: t(`state.${String(key)}`)
      }));
    });

    function putFilterDebounced(filter: Partial<Filters>): void {
      if (timerId.value) {
        clearTimeout(timerId.value);
      }
      stationStore.putFilter(filter);
      timerId.value = setTimeout(async () => {
        await stationStore.fetchFilteredStations();
      }, 500);
    }

    function cleanString(value: string): string | undefined {
      return value === '' ? undefined : value;
    }

    function cleanDate(date: Date | null): Date | undefined {
      return date ?? undefined;
    }

    function cleanStates(states: string[]): string[] | undefined {
      return states.length > 0 ? states : undefined;
    }

    function cleanAltitude(altitude: [number, number]): [number, number] | undefined {
      const altitudeRange = stationDatasetStore.metadata?.stationAltitudeRange;
      return altitudeRange && scalarArraysAreEqual(altitude, altitudeRange)
        ? undefined
        : altitude;
    }

    function cleanBoolean(value: boolean | null): boolean | undefined {
      return value || undefined;
    }

    function toggleDrawer() {
      localShow.value = !localShow.value
    }

    watch(
      () => stationStore.filters,
      (newFilters) => {
        localFilters.value = { ...newFilters, altitude: localFilters.value.altitude ?? newFilters.altitude };
      },
      { deep: true }
    );

    return {
      t,
      stationStore,
      datasetStore,
      stationDatasetStore,
      localFilters,
      localizedStates,
      defaultAltitude,
      altitudeFormatted,
      putFilterDebounced,
      cleanString,
      cleanDate,
      cleanStates,
      cleanAltitude,
      cleanBoolean,
      toggleDrawer,
      localShow,
    };
  },
});
</script>

<style scoped>
.sidesheet {
  height: 100%;
  overflow-y: auto;
}

.filter-drawer {
  padding: 0;
  margin-left: -16px;
  margin-right: 16px;
  min-width: 312px;
  top: 0 !important;
  height: calc(100%) !important;
}
</style>
